/* eslint-disable react/sort-comp, no-return-assign, no-param-reassign */
import classnames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import WordPiece from 'es6/components/apps/sentence-builder/word-piece';

const INITIAL_STATE = {
  draggedOver: false,
};

export class DropZone extends React.Component {
  constructor(props) {
    super(props);
    this.state = this._getInitialState();
  }

  _getInitialState() {
    return INITIAL_STATE;
  }

  /**
   * Gets the properties for the DropZone container.
   *
   * @returns {Object}
   */
  _getContainerProps() {
    const { draggedOver } = this.state;

    const className = classnames('drop-zone', {
      'dragged-over': draggedOver,
    }, this.props.className);

    const { disabled, expectedValue } = this.props;

    return {
      className,
      'data-expected': expectedValue,
      onDragEnter: !disabled ? this._handleDragEnter.bind(this) : null,
      onDragOver: !disabled ? this._handleDragOver.bind(this) : null,
      onDragLeave: this._handleDragLeave.bind(this),
      onDrop: !disabled ? this._handleDrop.bind(this) : null,
    };
  }

  /**
   * Helper to send the word choice back to
   *
   * @param {Function} handler
   * @param {Event} e             the original event
   */
  _executeHandler(handler, e) {
    const wordChoice = WordPiece.getWordChoice();

    if (handler) {
      handler.call(this, wordChoice, e);
    }
  }

  /**
   * When entering the DropZone, set the pointer events on the children to be
   * null, so they do not interfere with the DragLeave event.
   *
   * @param {Event} e
   */
  _handleDragEnter(e) {
    e.preventDefault();

    const domNode = ReactDOM.findDOMNode(this);
    const draggedElement = WordPiece.getDraggedElement();

    // Don't disable pointer events on children if the children are
    // inside the DOM container.
    if (!domNode.contains(draggedElement)) {
      this._setPointerEvents('none');
    }
  }

  /**
   * DOMNode is only accessible within the context of the component.
   * Allow the drag and drop methods to be extended after local methods have run.
   *
   * @param {Event} e
   */
  _handleDragOver(e) {
    e.preventDefault();

    this.setState({ draggedOver: true });
    this._executeHandler(this.props.onDragOver, e);
  }

  /**
   * Callback for when leaving the drop zone.
   *
   * @param {Event} e
   */
  _handleDragLeave(e) {
    e.preventDefault();

    // When removing pointer events on the children, there may
    // be an dragleave event triggered on the children. For this
    // reason, make sure the target is on the parent.
    if (e.target !== ReactDOM.findDOMNode(this)) {
      return;
    }

    this._resetState();
    this._executeHandler(this.props.onDragLeave, e);
  }

  /**
   * Callback for when something is dropped on the drop zone.
   *
   * @param {Event} e
   */
  _handleDrop(e) {
    e.preventDefault();
    this._resetState();
    this._executeHandler(this.props.onDrop, e);
  }

  render() {
    const props = this._getContainerProps();
    return (<div {...props}>{this.props.children}</div>);
  }

  _resetState() {
    this.setState(this._getInitialState());
    this._setPointerEvents('');
  }

  /**
   * Sets pointer events on the children of the drop zone.
   *
   * Drag leave is pretty hacky and does not even work consistently across
   * browsers: https://bugs.webkit.org/show_bug.cgi?id=66547
   *
   * The best solution to preventing dragleave from triggering on children
   * is to disable the pointer events.
   *
   * @param {String} value
   */
  _setPointerEvents(value) {
    const childNodes = [...ReactDOM.findDOMNode(this).childNodes];
    childNodes.forEach(childNode => childNode.style.pointerEvents = value);
  }
}

DropZone.propTypes = Object.freeze({
  className: React.PropTypes.string,
  disabled: React.PropTypes.bool,
  expectedValue: React.PropTypes.string,
  onDragEnter: React.PropTypes.func,
  onDragLeave: React.PropTypes.func,
  onDragOver: React.PropTypes.func,
  onDrop: React.PropTypes.func,
});

DropZone.defaultProps = Object.freeze({
  className: '',
  disabled: false,
});

export default DropZone;
